// Empire Apps Chassis · elder · remote-pin
// Children pin shortcuts on parent's home grid remotely.
// Cross-device sync via BroadcastChannel (same device) + Pages Function bridge (cross-device, stubbed).
// Pending-pins queue with parent-side accept / dismiss flow.

import type { LangCode } from "./types.ts";
import { screenReaderAnnounce } from "./a11y.ts";
import { makeBilingual } from "./language.ts";
import { i18n } from "./i18n-strings.ts";

declare const __pin_brand: unique symbol;
export type PinId = string & { readonly [__pin_brand]: "PinId" };

export type PinStatus = "pending" | "accepted" | "dismissed";

export interface PinShortcut {
  readonly id: PinId;
  readonly label: string;
  readonly icon: string;         // single emoji-like glyph OR data-URI svg
  readonly actionUrl: string;    // canonical URL (sarathi://upi/maid OR https://wa.me/91...)
  readonly fromLabel: string;    // "Aditya" — who sent it
  readonly fromContactId: string;
  readonly issuedAt: number;
  readonly status: PinStatus;
  readonly statusChangedAt: number | null;
}

const PIN_STORE_KEY = "empire.elder.pins";
const CHANNEL_NAME = "empire-elder-pins";

function safeStorage(): Storage | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
}

function newPinId(): PinId {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID() as PinId;
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}` as PinId;
}

export function listPins(): readonly PinShortcut[] {
  const s = safeStorage();
  if (!s) return [];
  const raw = s.getItem(PIN_STORE_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as readonly PinShortcut[];
  } catch {
    return [];
  }
}

function writeAll(pins: readonly PinShortcut[]): void {
  const s = safeStorage();
  if (!s) return;
  s.setItem(PIN_STORE_KEY, JSON.stringify(pins.slice(-100))); // cap at 100
  broadcastChange("update");
}

function broadcastChange(kind: "create" | "update" | "remove"): void {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage({ kind, at: Date.now() });
    ch.close();
  } catch {
    // BroadcastChannel unsupported (older browsers / SSR)
  }
}

export function listPending(): readonly PinShortcut[] {
  return listPins().filter((p) => p.status === "pending");
}

export function listAccepted(): readonly PinShortcut[] {
  return listPins().filter((p) => p.status === "accepted");
}

export interface PinDraft {
  readonly label: string;
  readonly icon: string;
  readonly actionUrl: string;
  readonly fromLabel: string;
  readonly fromContactId: string;
}

export function enqueuePin(draft: PinDraft): PinShortcut {
  const allowed = isUrlSafe(draft.actionUrl);
  if (!allowed.ok) throw new Error(`pin.action_url rejected: ${allowed.reason}`);
  const pin: PinShortcut = {
    id: newPinId(),
    label: draft.label.slice(0, 28),
    icon: draft.icon.slice(0, 4),
    actionUrl: draft.actionUrl,
    fromLabel: draft.fromLabel.slice(0, 32),
    fromContactId: draft.fromContactId,
    issuedAt: Date.now(),
    status: "pending",
    statusChangedAt: null,
  };
  const current = listPins();
  writeAll([...current, pin]);
  return pin;
}

export function acceptPin(id: PinId): PinShortcut | null {
  const current = listPins();
  const idx = current.findIndex((p) => p.id === id);
  if (idx < 0) return null;
  const cur = current[idx];
  if (!cur || cur.status !== "pending") return cur ?? null;
  const next: PinShortcut = { ...cur, status: "accepted", statusChangedAt: Date.now() };
  writeAll([...current.slice(0, idx), next, ...current.slice(idx + 1)]);
  return next;
}

export function dismissPin(id: PinId): PinShortcut | null {
  const current = listPins();
  const idx = current.findIndex((p) => p.id === id);
  if (idx < 0) return null;
  const cur = current[idx];
  if (!cur) return null;
  const next: PinShortcut = { ...cur, status: "dismissed", statusChangedAt: Date.now() };
  writeAll([...current.slice(0, idx), next, ...current.slice(idx + 1)]);
  return next;
}

export function removePin(id: PinId): void {
  const current = listPins();
  writeAll(current.filter((p) => p.id !== id));
  broadcastChange("remove");
}

export interface PinAcceptResult {
  readonly accepted: PinShortcut;
  readonly homeGridUpdated: boolean;
}

export function subscribeToPinChanges(cb: () => void): () => void {
  if (typeof BroadcastChannel === "undefined") return () => undefined;
  const ch = new BroadcastChannel(CHANNEL_NAME);
  const handler = (): void => cb();
  ch.addEventListener("message", handler);
  return () => {
    ch.removeEventListener("message", handler);
    ch.close();
  };
}

// --- URL safety gate
// Sarathi pins are remote-initiated, so we strictly allowlist scheme + host shape.

interface UrlSafety {
  readonly ok: boolean;
  readonly reason: string;
}

function isUrlSafe(raw: string): UrlSafety {
  if (!raw || raw.length > 512) return { ok: false, reason: "too-long-or-empty" };
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return { ok: false, reason: "not-a-url" };
  }
  const allowedSchemes = new Set([
    "https:",
    "tel:",
    "mailto:",
    "sarathi:",
    "upi:",        // upi://pay?pa=...
    "intent:",     // android intents (Capacitor)
  ]);
  if (!allowedSchemes.has(u.protocol)) return { ok: false, reason: "scheme-not-allowed" };
  // Disallow javascript:, data:, file: explicitly (covered by allowlist but be explicit)
  if (/^(javascript|data|file|vbscript|blob):$/i.test(u.protocol)) {
    return { ok: false, reason: "dangerous-scheme" };
  }
  return { ok: true, reason: "" };
}

// --- Bridge stub for cross-device sync
// In production this would POST to a CF Pages Function that propagates to the parent's device via push.
// For v0 the bridge is a no-op stub — same-device-only via BroadcastChannel.

export interface PinBridgeConfig {
  readonly endpointUrl: string | null;
  readonly empireIdToken: string | null;
}

let bridge: PinBridgeConfig = { endpointUrl: null, empireIdToken: null };

export function configureBridge(cfg: PinBridgeConfig): void {
  bridge = cfg;
}

export async function pushPinAcrossDevices(pin: PinShortcut): Promise<{ ok: boolean; via: "bridge" | "stub" }> {
  if (!bridge.endpointUrl || !bridge.empireIdToken) {
    // stub
    return { ok: true, via: "stub" };
  }
  try {
    const res = await fetch(bridge.endpointUrl, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${bridge.empireIdToken}`,
      },
      body: JSON.stringify(pin),
    });
    return { ok: res.ok, via: "bridge" };
  } catch {
    return { ok: false, via: "bridge" };
  }
}

export function announceNewPin(pin: PinShortcut, lang: LangCode): void {
  const msg = makeBilingual(i18n, "pin.new_request", lang).primary;
  screenReaderAnnounce(`${pin.fromLabel}: ${pin.label}. ${msg}`, { polite: false, clearAfterMs: 9000 });
}
